import {
  HUBBELL_FEEDBACK_DIVISIONS,
  type RenewalRuleKind,
  type RenewalRuleType,
} from '@erp/rpc';

import type { TranslateFn } from 'src/lib/pricelist-utils';
import type { RenewalRuleDraft } from './renewalRuleForm';

/**
 * Display text for Renewal Rules, given gt-react's `gt`: kind names, a rule
 * read as a plain-language sentence, its Division scope, and the pricing
 * team's standard rules.
 */

type KindOf<K extends RenewalRuleType> = Extract<RenewalRuleKind, { type: K }>;

/** "$0.005": the tolerance as the pricing team writes it. */
function formatDollars(amount: number): string {
  return `$${amount}`;
}

/** 0.1 → "10%". */
export function formatPercent(fraction: number): string {
  return `${Number((fraction * 100).toPrecision(12))}%`;
}

export function renewalRuleKindNames(
  gt: TranslateFn,
): Record<RenewalRuleType, string> {
  const ctx = { context: 'kind of Mass Renewal pricing rule' };
  return {
    holdWhenCostUnchanged: gt('Hold when cost unchanged', ctx),
    marginFloor: gt('Margin floor', ctx),
    holdListedItems: gt('Hold listed items', ctx),
  };
}

type Describers = {
  [K in RenewalRuleType]: (kind: KindOf<K>) => string;
};

/** One sentence per kind: a new kind is one entry. */
function describers(gt: TranslateFn): Describers {
  return {
    holdWhenCostUnchanged: (kind) =>
      gt(
        'Hold margin and core cost when the cost did not change (±{tolerance})',
        {
          tolerance: formatDollars(kind.tolerance),
          context:
            'Mass Renewal rule summary; tolerance is a dollar amount like $0.005',
        },
      ),
    marginFloor: (kind) =>
      gt('Never decrease a margin below {floor}', {
        floor: formatPercent(kind.floor),
        context: 'Mass Renewal rule summary; floor is a percent like 10%',
      }),
    holdListedItems: (kind) =>
      kind.items.length === 1
        ? gt('Hold 1 listed item', {
            context:
              'Mass Renewal rule summary: one AS400 item is never changed',
          })
        : gt('Hold {count} listed items', {
            count: kind.items.length,
            context:
              'Mass Renewal rule summary: listed AS400 items are never changed',
          }),
  };
}

function describeKind<K extends RenewalRuleType>(
  type: K,
  kind: KindOf<K>,
  gt: TranslateFn,
): string {
  return describers(gt)[type](kind);
}

/** The rule as a sentence, e.g. "Never decrease a margin below 10%". */
export function describeRenewalRule(
  kind: RenewalRuleKind,
  gt: TranslateFn,
): string {
  return describeKind(kind.type, kind, gt);
}

/** "N. West (WT)" for a tab letter; the letter itself when unknown. */
export function divisionLabel(letter: string): string {
  const division = HUBBELL_FEEDBACK_DIVISIONS.find((d) => d.letter === letter);
  return division ? `${division.name} (${division.code})` : letter;
}

/** "All Divisions", or the Divisions by name and code in table order. */
export function divisionScope(divisions: string[], gt: TranslateFn): string {
  if (divisions.length === 0) {
    return gt('All Divisions', {
      context: 'a Mass Renewal rule applies to every Division',
    });
  }
  const scoped = new Set(divisions);
  return HUBBELL_FEEDBACK_DIVISIONS.flatMap(({ letter }) =>
    scoped.has(letter) ? [divisionLabel(letter)] : [],
  ).join(', ');
}

/**
 * The pricing team's standing rules that need no list (2026-10-29): hold
 * when the cost did not change (±$0.005) and a 10% margin floor, every
 * Division. The sensitive list is a third rule but needs its items.
 */
export function standardRenewalRules(gt: TranslateFn): RenewalRuleDraft[] {
  const ctx = { context: 'name of a standard Mass Renewal rule' };
  return [
    {
      name: gt('Hold when cost unchanged', ctx),
      divisions: [],
      rule: { type: 'holdWhenCostUnchanged', tolerance: 0.005 },
    },
    {
      name: gt('Margin floor 10%', ctx),
      divisions: [],
      rule: { type: 'marginFloor', floor: 0.1 },
    },
  ];
}

/** The name the sensitive-list rule starts with. */
export function sensitiveListName(gt: TranslateFn): string {
  return gt('Sensitive items', {
    context: 'name of a standard Mass Renewal rule that holds listed items',
  });
}
